import { XIcon } from 'lucide-react-native';
import { Pressable } from 'react-native';
import { View, YStack } from 'tamagui';

import { type ColorTokenKey } from '../../themes/SmoothieColorTokens';
import { hitSlop } from '../componentHelpers';
import { Icon } from '../icons/Icon';
import { BodyMediumEmphasized, BodySmallRegular } from '../text/Typography';

export type NoticeVariant = 'neutral' | 'positive' | 'warning' | 'danger';

const HeaderColor: Record<NoticeVariant, ColorTokenKey> = {
    neutral: '$gray1200',
    positive: '$green1100',
    warning: '$orange1100',
    danger: '$red1100',
};

const ContentColor: Record<NoticeVariant, ColorTokenKey> = {
    neutral: '$gray1000',
    positive: '$green1000',
    warning: '$orange1000',
    danger: '$red1000',
};

const BackgroundColor: Record<NoticeVariant, ColorTokenKey> = {
    neutral: '$gray200',
    positive: '$green300',
    warning: '$orange300',
    danger: '$red200',
};

interface NoticeProps {
    variant: NoticeVariant;
    title: string;
    description: string;
    action?: {
        text: string;
        onPress: () => void;
        underlined?: boolean;
    };
    onDismiss?: () => void;
}

export const Notice = ({
    variant,
    title,
    description,
    action,
    onDismiss,
}: NoticeProps) => {
    const headerColor = HeaderColor[variant];
    const contentColor = ContentColor[variant];
    const backgroundColor = BackgroundColor[variant];

    return (
        <YStack
            padding={16}
            gap={16}
            borderRadius={16}
            backgroundColor={backgroundColor}
            alignItems="flex-start"
            position="relative"
        >
            {onDismiss && (
                <View
                    position="absolute"
                    top={16}
                    right={16}
                    zIndex={10}
                    onPress={onDismiss}
                    cursor="pointer"
                    {...hitSlop(16)}
                >
                    <Icon icon={XIcon} color={headerColor} />
                </View>
            )}
            <YStack gap={8} paddingRight={onDismiss ? 32 : 0} width="100%">
                <BodyMediumEmphasized color={headerColor}>
                    {title}
                </BodyMediumEmphasized>
                <BodySmallRegular color={contentColor}>
                    {description}
                </BodySmallRegular>
            </YStack>
            {action && (
                <Pressable onPress={action.onPress}>
                    <BodyMediumEmphasized
                        textDecorationLine={
                            action.underlined !== false ? 'underline' : 'none'
                        }
                        color={headerColor}
                    >
                        {action.text}
                    </BodyMediumEmphasized>
                </Pressable>
            )}
        </YStack>
    );
};
